import React from 'react'
import PropTypes from 'prop-types'
import { Card, CardBody, Col, Row } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCoins, faFire, faUsers } from '@fortawesome/free-solid-svg-icons'
import Flex from '../../common/Flex'
import { clampPercent, formatNumber, formatPercent, formatUsd, getLevelStyle } from '../../rewards/rewardsUtils'

const StatCard = ({ icon, color, title, value, detail }) => (
  <Card className="h-100">
    <CardBody>
      <Flex align="center" className="mb-2">
        <div
          className={`d-flex flex-center rounded-circle mr-2 flex-shrink-0 bg-soft-${color} text-${color}`}
          style={{ width: '2.25rem', height: '2.25rem' }}
        >
          <FontAwesomeIcon icon={icon} />
        </div>
        <h6 className="text-600 mb-0">{title}</h6>
      </Flex>
      <p className="fs-2 font-weight-bold text-900 mb-0">{value}</p>
      <p className="fs--1 text-600 mb-0">{detail}</p>
    </CardBody>
  </Card>
)

// Distribución por nivel de las propiedades que participan. Cada nivel lleva
// el color de su insignia, el mismo que ve el propietario en su vista.
const LevelDistribution = ({ levels, participants }) => (
  <Card className="h-100">
    <CardBody>
      <h6 className="text-600 mb-3">Propietarios por nivel</h6>
      {[...levels]
        .sort((a, b) => a.order - b.order)
        .map((level) => {
          const count = Number(level.properties) || 0
          const share = participants > 0 ? (count / participants) * 100 : 0
          const style = getLevelStyle(level.badge_icon)
          return (
            <div key={level.id} className="mb-3">
              <Flex justify="between" className="fs--1 mb-1">
                <span className="font-weight-semi-bold text-900">{level.name}</span>
                <span className="text-600">
                  {formatNumber(count)} ({formatPercent(share, 0)})
                </span>
              </Flex>
              <div className="rounded-capsule bg-200" style={{ height: '0.5rem' }}>
                <div
                  className="rounded-capsule h-100"
                  style={{ width: `${clampPercent(share)}%`, background: style.color }}
                />
              </div>
            </div>
          )
        })}
    </CardBody>
  </Card>
)

// HO-TASK-288: adopción del programa en el condominio. Todo agregado: la vista
// del administrador no muestra saldos ni datos de ningún propietario.
const AdoptionStats = ({ summary }) => {
  const total = Number(summary.properties_total) || 0
  const participants = Number(summary.participants) || 0
  const participation = total > 0 ? (participants / total) * 100 : 0
  const { streaks = {}, coins = {} } = summary

  return (
    <Row noGutters>
      <Col md={6} lg={3} className="mb-3 pr-md-2">
        <StatCard
          icon={faUsers}
          color="primary"
          title="Participación"
          value={formatPercent(participation, 0)}
          detail={`${formatNumber(participants)} de ${formatNumber(total)} propiedades ya ganaron Coins.`}
        />
      </Col>
      <Col md={6} lg={3} className="mb-3 pl-md-2 pr-lg-2">
        <StatCard
          icon={faFire}
          color="danger"
          title="Rachas activas"
          value={formatNumber(streaks.active)}
          detail={
            Number(streaks.active) > 0
              ? `Promedio de ${formatNumber(streaks.average_months, 1)} meses seguidos pagando a tiempo; la más larga, ${formatNumber(streaks.best_months)}.`
              : 'Todavía nadie encadena meses pagando a tiempo.'
          }
        />
      </Col>
      <Col md={6} lg={3} className="mb-3 pr-md-2 pl-lg-2">
        <StatCard
          icon={faCoins}
          color="warning"
          title="Coins canjeados"
          value={`${formatNumber(coins.redeemed_hc)} HC`}
          detail={`${formatUsd(coins.redeemed_usd)} abonados a cuotas en ${formatNumber(coins.redemptions)} ${
            Number(coins.redemptions) === 1 ? 'canje' : 'canjes'
          }. Ganados en total: ${formatNumber(coins.earned_hc)} HC.`}
        />
      </Col>
      <Col md={6} lg={3} className="mb-3 pl-md-2">
        <LevelDistribution levels={summary.levels || []} participants={participants} />
      </Col>
    </Row>
  )
}

AdoptionStats.propTypes = { summary: PropTypes.object.isRequired }

export default AdoptionStats
